/**
 * Error tracking: console in development; Sentry in production when configured.
 */

import { initBrowserSentry, isBrowserSentryActive, captureBrowserException } from "./sentry";
import { normalizeToError, getErrorTrackingExtras } from "./normalizeError";

/**
 * Track application error
 */
export function trackError(error: Error, context?: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    console.error("Error occurred:", error.message, {
      stack: error.stack,
      ...context,
    });
  } else {
    console.error("Error:", error.message, context);
  }

  if (isBrowserSentryActive()) {
    const extras = getErrorTrackingExtras(error);
    captureBrowserException(error, extras ? { ...extras, ...context } : context);
  }
}

/**
 * Track React error boundary errors
 */
export function trackBoundaryError(error: Error, errorInfo: { componentStack?: string | null }) {
  trackError(error, {
    componentStack: errorInfo.componentStack,
  });
}

/**
 * Initialize global error handlers
 */
export function initErrorTracking() {
  initBrowserSentry();

  if (typeof window === "undefined") return;

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    console.error("Unhandled promise rejection:", event.reason);
    trackError(normalizeToError(event.reason));
  });

  // Handle global errors
  window.addEventListener("error", (event) => {
    console.error("Global error:", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });

    if (event.error instanceof Error) {
      trackError(event.error, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    } else if (event.error != null) {
      trackError(normalizeToError(event.error), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    }
  });
}

/**
 * Track user action with error handling
 */
export function trackAction<T>(
  actionName: string,
  action: () => T,
  onError?: (error: Error) => void
): T | undefined {
  try {
    const result = action();
    return result;
  } catch (error) {
    const err = normalizeToError(error);
    trackError(err, { action: actionName });
    onError?.(err);
    return undefined;
  }
}

/**
 * Track async action with error handling
 */
export async function trackAsyncAction<T>(
  actionName: string,
  action: () => Promise<T>,
  onError?: (error: Error) => void
): Promise<T | undefined> {
  try {
    const result = await action();
    return result;
  } catch (error) {
    const err = normalizeToError(error);
    trackError(err, { action: actionName });
    onError?.(err);
    return undefined;
  }
}
